// src/services/api/mealService.ts
import { apiClient } from './config';
import { IMealService, Meal, Order, Shift, TakeawayOrder } from '../interfaces';

export class MealService implements IMealService {
  private static instance: MealService;
  private readonly cache: Map<string, { data: any; timestamp: number }> =
    new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  static getInstance(): MealService {
    if (!MealService.instance) {
      MealService.instance = new MealService();
    }
    return MealService.instance;
  }

  private isCacheValid(key: string): boolean {
    const cached = this.cache.get(key);
    if (!cached) return false;
    return Date.now() - cached.timestamp < this.CACHE_DURATION;
  }

  async getMeals(): Promise<Meal[]> {
    const cacheKey = 'meals';
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    // Updated to use the appropriate endpoint
    const response = await apiClient.get<Meal[]>('/api/user/meals');
    this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
    return response.data;
  }

  async getMealsByDate(count: number): Promise<{ [key: string]: Meal[] }> {
    const cacheKey = `mealsByDate-${count}`;
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    const response = await apiClient.get<{ [key: string]: Meal[] }>(
      `/api/user/meals-by-date?count=${count}`
    );
    this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
    return response.data;
  }

  async getNextBusinessDays(
    count: number
  ): Promise<{ date: string; dayName: string }[]> {
    // Updated to use the new endpoint
    const response = await apiClient.get<{ date: string; dayName: string }[]>(
      `/api/user/business-days?count=${count}`
    );
    return response.data;
  }

  async preOrderMeals(orders: Omit<Order, 'id'>[]): Promise<boolean> {
    // Updated to use the appropriate endpoint
    await apiClient.post('/api/user/orders/bulk', orders);
    return true;
  }

  async getOrders(): Promise<Order[]> {
    // This should use the user's orders endpoint
    const response = await apiClient.get<Order[]>('/api/user/orders');
    return response.data;
  }

  async getExistingPreOrders(): Promise<{ [key: string]: number }> {
    // Updated to use the appropriate endpoint
    const response = await apiClient.get<Order[]>(
      '/api/user/orders/pre-orders'
    );
    const preOrders: { [key: string]: number } = {};
    response.data.forEach((order) => {
      preOrders[order.date] = order.mealId;
    });
    return preOrders;
  }

  async getMealById(id: number): Promise<Meal> {
    const cacheKey = `meal-${id}`;
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    // Updated to use the appropriate endpoint
    const response = await apiClient.get<Meal>(`/api/user/meals/${id}`);
    this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
    return response.data;
  }

  async getOrderHistory(): Promise<Order[]> {
    // Updated to use the appropriate endpoint
    const response = await apiClient.get<Order[]>('/api/user/orders/history');
    return response.data;
  }

  async deletePreOrder(orderId: number): Promise<boolean> {
    // Updated to use the appropriate endpoint
    await apiClient.delete(`/api/user/orders/${orderId}`);
    return true;
  }

  async createTakeawayOrder(
    order: Omit<TakeawayOrder, 'id' | 'status'>
  ): Promise<boolean> {
    try {
      // Updated to use the appropriate endpoint
      await apiClient.post('/api/user/orders/takeaway', order);
      // Invalidate relevant caches
      this.cache.delete('takeawayOrders');
      this.cache.delete('activeTakeawayOrders');
      return true;
    } catch (error) {
      console.error('Failed to create takeaway order:', error);
      throw error;
    }
  }

  async getTakeawayOrders(): Promise<TakeawayOrder[]> {
    const cacheKey = 'takeawayOrders';
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    try {
      // Updated to use the appropriate endpoint
      const response = await apiClient.get<TakeawayOrder[]>(
        '/api/user/orders/takeaway'
      );
      this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch takeaway orders:', error);
      throw error;
    }
  }

  async getActiveTakeawayOrders(): Promise<TakeawayOrder[]> {
    const cacheKey = 'activeTakeawayOrders';
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    try {
      // Updated to use the appropriate endpoint
      const response = await apiClient.get<TakeawayOrder[]>(
        '/api/user/orders/takeaway/active'
      );
      this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch active takeaway orders:', error);
      throw error;
    }
  }

  async cancelTakeawayOrder(orderId: number): Promise<boolean> {
    try {
      // Updated to use the appropriate endpoint
      await apiClient.post(`/api/user/orders/takeaway/${orderId}/cancel`);
      // Invalidate relevant caches
      this.cache.delete('takeawayOrders');
      this.cache.delete('activeTakeawayOrders');
      return true;
    } catch (error) {
      console.error('Failed to cancel takeaway order:', error);
      throw error;
    }
  }

  async confirmDelivery(orderId: number): Promise<boolean> {
    try {
      // Updated to use the appropriate endpoint
      const response = await apiClient.post(
        `/api/user/orders/${orderId}/confirm-delivery`,
        {
          deliveredAt: new Date().toISOString(),
        }
      );
      return response.data.success;
    } catch (error) {
      console.error('Failed to confirm delivery:', error);
      throw error;
    }
  }

  async getOrderStatus(
    orderId: number
  ): Promise<{ isDelivered: boolean; deliveredAt?: string }> {
    try {
      // Updated to use the appropriate endpoint
      const response = await apiClient.get(
        `/api/user/orders/${orderId}/status`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get order status:', error);
      throw error;
    }
  }

  async getShifts(): Promise<Shift[]> {
    const cacheKey = 'shifts';
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    try {
      const response = await apiClient.get<Shift[]>('/api/user/shifts');
      this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch shifts:', error);
      return [];
    }
  }
}
